// src/components/MypageFooter.tsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { FaLine, FaWeixin } from 'react-icons/fa';
import api from '../api/axios';
import BindEmailDialog from './BindEmailDialog';
import { toast } from 'react-toastify';

/** 認証プロバイダ型 */
type Provider = 'email' | 'wechat' | 'line' | 'phone' | 'unknown';

type Props = {
  /** パスワード変更ボタン押下時（親でダイアログを開く） */
  onClickChangePassword: () => void;
  /** クーポン表示ボタン押下時（親でダイアログを開く） */
  onClickOpenCoupons: () => void;
  /** ログアウト押下時（親でセッション後処理） */
  onLogout: () => void;
};

/**
 * マイページ下部の操作フッター（再利用可能）
 * - メールユーザーと電話ユーザーのみ「パスワードを変更」を表示
 * - WeChat ログインは非表示
 * - 親の白カードに合わせて灰底＆下側の角丸
 */
const MypageFooter: React.FC<Props> = ({ onClickChangePassword, onClickOpenCoupons, onLogout }) => {
  const { t } = useTranslation();

  // ---- 認証情報（/auth/me から判定）----
  const [provider, setProvider] = useState<Provider>('unknown');
  const [hasEmail, setHasEmail] = useState<boolean>(false);
  const [hasLine, setHasLine] = useState<boolean>(false);
  const [hasWechat, setHasWechat] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/auth/me');
        const data = res.data || {};
        let prov: Provider = 'unknown';
        if (data?.provider === 'wechat') prov = 'wechat';
        else if (data?.provider === 'line') prov = 'line';
        else if (data?.provider === 'phone') prov = 'phone';
        else if (data?.provider === 'email') prov = 'email';
        else if (data?.email) prov = 'email';
        setProvider(prov);
        setHasEmail(!!data?.email);
        setHasLine(!!data?.line_user_id);
        setHasWechat(!!data?.wechat_openid);
      } catch {
        // 未ログイン等は何もしない
      }
    })();
  }, []);

  // メールバインドダイアログ
  const [showBindEmailDlg, setShowBindEmailDlg] = useState(false);

  // LINE 連携処理（LIFF で id_token 取得 → バックエンドへ）
  const handleBindLine = async () => {
    const liffId = process.env.REACT_APP_LINE_LIFF_ID;
    if (!liffId) { toast.error('LINE LIFF ID not configured'); return; }
    try {
      const liff = (await import('@line/liff')).default;
      await liff.init({ liffId });
      if (!liff.isLoggedIn()) { liff.login({ redirectUri: window.location.href }); return; }
      const idToken = liff.getIDToken();
      if (!idToken) throw new Error('No ID Token');
      await api.post('/auth/bind_line', { id_token: idToken });
      setHasLine(true);
      toast.success(t('profile.line_bind_success') || 'LINE を連携しました');
    } catch (err: any) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : (t('profile.line_bind_fail') || 'LINE 連携に失敗しました'));
    }
  };

  // WeChat 連携（WeChat OAuth にリダイレクト。JWT を query param で渡す）
  const handleBindWechat = () => {
    const token = localStorage.getItem('token');
    if (!token) { toast.error('ログインが必要です'); return; }
    window.location.href = `/api/wechat/bind?token=${encodeURIComponent(token)}`;
  };

  const showChangePassword = provider === 'email' || provider === 'phone' || ((provider === 'wechat' || provider === 'line') && hasEmail);
  const showBindEmail = !hasEmail && (provider === 'wechat' || provider === 'line');

  return (
    <>
    <footer
      role="contentinfo"
      className="mt-8 w-full border-t border-slate-100 pt-6 pb-2 flex flex-wrap items-center justify-center sm:justify-end gap-3"
    >
      {/* メール連携（WeChat/LINEユーザーのみ） */}
      {showBindEmail && (
        <button
          onClick={() => setShowBindEmailDlg(true)}
          className="text-sm border border-coral/40 bg-coral/5 text-coral px-5 py-2 rounded-full hover:bg-coral/10 transition-colors flex items-center gap-1.5"
        >
          <Mail className="w-3.5 h-3.5" />
          {t('login_success.bind_email_btn')}
        </button>
      )}

      {/* LINE / WeChat 連携ボタン: 調査中のため一時非表示 */}

      {/* パスワード変更 */}
      {showChangePassword && (
        <button
          onClick={onClickChangePassword}
          className="text-sm border border-slate-200 bg-white text-slate-600 px-5 py-2 rounded-full hover:bg-slate-50 hover:border-slate-300 transition-colors"
        >
          {t('password.change')}
        </button>
      )}

      {/* クーポンを見る */}
      <button
        onClick={onClickOpenCoupons}
        className="text-sm border border-slate-200 bg-white text-slate-600 px-5 py-2 rounded-full hover:bg-slate-50 hover:border-slate-300 transition-colors"
        title={t('mypage.view_coupons_tip') || '保有クーポンを確認'}
      >
        {t('mypage.view_coupons') || 'クーポンを見る'}
      </button>

      {/* ログアウト */}
      <button
        onClick={onLogout}
        className="text-sm bg-slate-100 text-slate-500 font-medium px-5 py-2 rounded-full hover:bg-slate-200 transition-colors"
      >
        {t('mypage.logout')}
      </button>
    </footer>

    <BindEmailDialog
      open={showBindEmailDlg}
      onClose={() => setShowBindEmailDlg(false)}
      onSuccess={() => {
        setHasEmail(true);
        setProvider('email');
        setShowBindEmailDlg(false);
      }}
    />
    </>
  );
};

export default MypageFooter;
